import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { showCustomToast } from '../common/CustomToast';
import './VoucherModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface VoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VoucherModal: React.FC<VoucherModalProps> = ({ isOpen, onClose }) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { token } = useSelector((state: RootState) => state.auth);

  if (!isOpen) return null;

  const handleRedeemVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!voucherCode.trim()) {
      showCustomToast.error('Please enter a voucher code');
      return;
    }

    setIsRedeeming(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/redeem-voucher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code: voucherCode.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        showCustomToast.success(data.message);
        setVoucherCode('');
        handleClose();
        // Refresh user balance
        window.location.reload();
      } else {
        showCustomToast.error(data.error || 'Failed to redeem voucher');
      }
    } catch (error) {
      console.error('Redeem voucher error:', error);
      showCustomToast.error('Failed to redeem voucher');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setVoucherCode('');
    }, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className={`voucher-modal-backdrop ${isClosing ? 'closing' : ''}`} onClick={handleBackdropClick}>
      <div className={`voucher-modal ${isClosing ? 'closing' : ''}`}>
        <div className="voucher-modal-header">
          <h2>Redeem Voucher</h2>
          <button className="close-btn" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="voucher-modal-body">
          <h3 className="voucher-title">Voucher Code</h3>
          <p className="voucher-description">
            Enter your voucher code below to redeem your reward
          </p>

          <form onSubmit={handleRedeemVoucher} className="voucher-form">
            <div className="voucher-input-section">
              <label className="voucher-label">Voucher Code</label>
              <div className="voucher-input-wrapper">
                <input
                  type="text"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="voucher-input"
                  placeholder="ENTER CODE"
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="modal-voucher-btn"
              disabled={isRedeeming || !voucherCode.trim()}
            >
              {isRedeeming ? (
                <>
                  <div className="btn-spinner"></div>
                  Redeeming...
                </>
              ) : (
                'Redeem Voucher'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VoucherModal;